import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

// Predefined highlight color options
export interface HighlightColor {
    id: string;
    name: string;
    fill: string;
    fillHighlighted: string;
    stroke: string;
    strokeHighlighted: string;
}

export const HIGHLIGHT_COLORS: HighlightColor[] = [
    {
        id: 'red',
        name: 'Red',
        fill: 'rgba(184, 65, 61, 0.2)',
        fillHighlighted: 'rgba(184, 65, 61, 0.35)',
        stroke: 'rgba(184, 65, 61, 0.5)',
        strokeHighlighted: 'rgba(184, 65, 61, 0.8)',
    },
    {
        id: 'blue',
        name: 'Blue',
        fill: 'rgba(59, 130, 246, 0.2)',
        fillHighlighted: 'rgba(59, 130, 246, 0.35)',
        stroke: 'rgba(59, 130, 246, 0.5)',
        strokeHighlighted: 'rgba(59, 130, 246, 0.8)',
    },
    {
        id: 'green',
        name: 'Green',
        fill: 'rgba(34, 197, 94, 0.2)',
        fillHighlighted: 'rgba(34, 197, 94, 0.35)',
        stroke: 'rgba(34, 197, 94, 0.5)',
        strokeHighlighted: 'rgba(34, 197, 94, 0.8)',
    },
    {
        id: 'purple',
        name: 'Purple',
        fill: 'rgba(147, 51, 234, 0.2)',
        fillHighlighted: 'rgba(147, 51, 234, 0.35)',
        stroke: 'rgba(147, 51, 234, 0.5)',
        strokeHighlighted: 'rgba(147, 51, 234, 0.8)',
    },
    {
        id: 'orange',
        name: 'Orange',
        fill: 'rgba(249, 115, 22, 0.2)',
        fillHighlighted: 'rgba(249, 115, 22, 0.35)',
        stroke: 'rgba(249, 115, 22, 0.5)',
        strokeHighlighted: 'rgba(249, 115, 22, 0.8)',
    },
    {
        id: 'teal',
        name: 'Teal',
        fill: 'rgba(20, 184, 166, 0.2)',
        fillHighlighted: 'rgba(20, 184, 166, 0.35)',
        stroke: 'rgba(20, 184, 166, 0.5)',
        strokeHighlighted: 'rgba(20, 184, 166, 0.8)',
    },
    {
        id: 'pink',
        name: 'Pink',
        fill: 'rgba(236, 72, 153, 0.2)',
        fillHighlighted: 'rgba(236, 72, 153, 0.35)',
        stroke: 'rgba(236, 72, 153, 0.5)',
        strokeHighlighted: 'rgba(236, 72, 153, 0.8)',
    },
    {
        id: 'yellow',
        name: 'Yellow',
        fill: 'rgba(234, 179, 8, 0.25)',
        fillHighlighted: 'rgba(234, 179, 8, 0.4)',
        stroke: 'rgba(234, 179, 8, 0.6)',
        strokeHighlighted: 'rgba(234, 179, 8, 0.9)',
    },
];

interface ThemeContextType {
    theme: Theme;
    resolvedTheme: ResolvedTheme;
    setTheme: (theme: Theme) => void;
    toggleTheme: () => void;
    highlightColor: HighlightColor;
    setHighlightColor: (colorId: string) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'quickmask-theme';
const HIGHLIGHT_COLOR_STORAGE_KEY = 'quickmask-highlight-color';

function getSystemTheme(): ResolvedTheme {
    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
}

function getStoredTheme(): Theme {
    if (typeof window !== 'undefined') {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
            return stored;
        }
    }
    return 'system';
}

function getStoredHighlightColor(): HighlightColor {
    if (typeof window !== 'undefined') {
        const stored = localStorage.getItem(HIGHLIGHT_COLOR_STORAGE_KEY);
        if (stored) {
            const found = HIGHLIGHT_COLORS.find(c => c.id === stored);
            if (found) return found;
        }
    }
    return HIGHLIGHT_COLORS[0]; // Default to red
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setThemeState] = useState<Theme>(() => getStoredTheme());
    const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
        const stored = getStoredTheme();
        return stored === 'system' ? getSystemTheme() : stored;
    });
    const [highlightColor, setHighlightColorState] = useState<HighlightColor>(() => getStoredHighlightColor());

    const applyTheme = useCallback((resolved: ResolvedTheme) => {
        const root = document.documentElement;
        if (resolved === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        setResolvedTheme(resolved);
    }, []);

    const setTheme = useCallback((newTheme: Theme) => {
        setThemeState(newTheme);
        localStorage.setItem(THEME_STORAGE_KEY, newTheme);
        const resolved = newTheme === 'system' ? getSystemTheme() : newTheme;
        applyTheme(resolved);
    }, [applyTheme]);

    const toggleTheme = useCallback(() => {
        const newTheme = resolvedTheme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
    }, [resolvedTheme, setTheme]);

    const setHighlightColor = useCallback((colorId: string) => {
        const color = HIGHLIGHT_COLORS.find(c => c.id === colorId);
        if (color) {
            setHighlightColorState(color);
            localStorage.setItem(HIGHLIGHT_COLOR_STORAGE_KEY, colorId);
        }
    }, []);

    // Apply initial theme and listen for system preference changes
    useEffect(() => {
        const resolved = theme === 'system' ? getSystemTheme() : theme;
        applyTheme(resolved);

        if (typeof window !== 'undefined' && window.matchMedia) {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleChange = (e: MediaQueryListEvent) => {
                if (theme === 'system') {
                    applyTheme(e.matches ? 'dark' : 'light');
                }
            };
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        }
    }, [theme, applyTheme]);

    // Keep the meta theme-color in sync
    useEffect(() => {
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute('content', resolvedTheme === 'dark' ? '#111827' : '#ffffff');
    }, [resolvedTheme]);

    return (
        <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme, highlightColor, setHighlightColor }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export default ThemeContext;
